import React from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { ReceiveSquare, TransmitSquare } from "iconsax-reactjs";
import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis } from "recharts";
import { fetchChartData } from "../../api/fetchers";
import currencyPairCheck from "../../utilities/currencyPairCheck";

export default function InflowOutflowCard() {
    const { business, currency: defaults } = useSelector((s) => s);
    const year = new Date().getFullYear();

    const { data, isLoading, error } = useQuery(
        ["chartData", year, defaults],
        () =>
            fetchChartData({
                businessId: business._id,
                currency: defaults,
            }),
        { enabled: !!business._id && !!defaults, staleTime: 5 * 60_000 }
    );

    if (isLoading) return <div className="bg-white p-6 rounded-xl col-span-1">Loading chart…</div>;
    if (error) {
        return (
            <div className="bg-white p-6 rounded-xl col-span-1 text-red-500">
                Failed to load chart
            </div>
        );
    }

    const inflowData = data?.inflow || [];
    const outflowData = data?.outflow || [];

    const total = (arr) => arr.reduce((s, p) => s + Number(p?.value || 0), 0);
    const inflowTotal = total(inflowData);
    const outflowTotal = total(outflowData);

    const { symbol, isCrypto } = currencyPairCheck(defaults, { returnType: "full" });

    const fmtTotal = (val) =>
        Number(val || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
    const leftValueInflow = isCrypto
        ? `${fmtTotal(inflowTotal)} ${symbol}`
        : `${symbol}${fmtTotal(inflowTotal)}`;
    const leftValueOutflow = isCrypto
        ? `${fmtTotal(outflowTotal)} ${symbol}`
        : `${symbol}${fmtTotal(outflowTotal)}`;

    const tooltipFormatter = (v) =>
        isCrypto ? `${fmtTotal(v)} ${symbol}` : `${symbol}${fmtTotal(v)}`;

    return (
        <div className="bg-white px-3 grid py-2 items-center md:px- rounded-xl col-span-1">
            {/* Inflow row */}
            <div className="py-6 flex items-center justify-between">
                <div className="pr-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ReceiveSquare size={20} color="#6F739B" variant="Bold" />
                        <span className="text-sm text-[#161827] font-[350]">Inflow</span>
                    </div>
                    <div className="text-[20px] md:text-[20px]  leading-none text-[#101828] font-[350]">
                        {leftValueInflow}
                    </div>
                </div>

                <div className="w-2/3 h-16">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={inflowData}>
                            <defs>
                                {/* green */}
                                <linearGradient id="gradInflow" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#22C55E" stopOpacity={0.28} />
                                    <stop offset="100%" stopColor="#22C55E" stopOpacity={0.04} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="time" hide />
                            <Tooltip
                                formatter={tooltipFormatter}
                                labelFormatter={(l) => l}
                                cursor={false}
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#22C55E"
                                strokeWidth={2}
                                fill="url(#gradInflow)"
                                dot={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* divider */}
            <div className="h-px bg-[#EEF0F6]" />

            {/* Outflow row */}
            <div className="py-6 flex items-center justify-between">
                <div className="pr-4">
                    <div className="flex items-center gap-2 mb-2">
                        <TransmitSquare size={20} color="#6F739B" variant="Bold" />
                        <span className="text-sm text-[#161827] font-[350]">Outflow</span>
                    </div>
                    <div className="text-[20px] md:text-[20px] leading-none text-[#101828] font-[350]">
                        {leftValueOutflow}
                    </div>
                </div>

                <div className="w-2/3 h-16">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={outflowData}>
                            <defs>
                                {/* blue */}
                                <linearGradient id="gradOutflow" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.26} />
                                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.04} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="time" hide />
                            <Tooltip
                                formatter={tooltipFormatter}
                                labelFormatter={(l) => l}
                                cursor={false}
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#3B82F6"
                                strokeWidth={2}
                                fill="url(#gradOutflow)"
                                dot={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
